import { useContext, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { DialogOpenChangeDetails } from '@chakra-ui/react';

import { useOpenClose } from '~/core/hooks/useOpenClose';
import { useCreateApplicationEmail } from '~/core/shared/api/middle-office/application';
import { useFormResolver } from '~/core/shared/hooks';
import { useEventHandler } from '~/core/shared/hooks/useEventHandler';
import { EmailCreateFormValues } from '~/core/shared/types';
import { useMiddleOfficeConfig } from '../../../../../contexts/middleOfficeConfig';
import { MessagesTabContext } from '../../../../application/tabs';
import { emailCreateDefaultFormValues } from '../../constants/emailCreateDefaultFormValues';
import { emailCreateValidationSchema } from '../../constants/emailCreateValidationSchema';
import { EmailCreateModalProps } from '../../types/EmailCreateModalProps';

export const useEmailCreateModal = (
  documentId: EmailCreateModalProps['documentId'],
  documentDefinition: EmailCreateModalProps['documentDefinition'],
) => {
  const { emailCreateModalApi } = useContext(MessagesTabContext);
  const unsavedChangesModalApi = useOpenClose();
  const middleOfficeConfig = useMiddleOfficeConfig();

  const mailToLabel = useMemo(
    () => middleOfficeConfig.getLabel('messageTab', 'mailTo', 'customer'),
    [middleOfficeConfig.getLabel],
  );

  // TODO: @haxxxton refactor to use form blocks
  const resolver = useFormResolver<EmailCreateFormValues>(
    emailCreateValidationSchema,
  );
  const form = useForm<EmailCreateFormValues>({
    defaultValues: emailCreateDefaultFormValues,
    resolver,
  });

  const { createEmail, isPending: isCreatingEmail } = useCreateApplicationEmail(
    documentId,
    documentDefinition,
  );

  const onSubmit = useEventHandler(
    async (formValues: EmailCreateFormValues): Promise<void> => {
      await createEmail(formValues);
      form.reset(emailCreateDefaultFormValues);
      emailCreateModalApi.onClose();
    },
  );

  const handleCloseAndReset = useEventHandler(() => {
    form.reset();
    unsavedChangesModalApi.onClose();
    emailCreateModalApi.onClose();
  });

  const handleClose = useEventHandler(() => {
    if (form.formState.isDirty) {
      unsavedChangesModalApi.onOpen();
    } else {
      handleCloseAndReset();
    }
  });

  // TODO: move to onClose prop of useOpenClose
  const onOpenChange = useEventHandler(({ open }: DialogOpenChangeDetails) => {
    if (open) return;
    handleClose();
  });

  return {
    mailToLabel,
    isCreatingEmail,
    emailCreateModalApi,
    form,
    handleClose,
    handleCloseAndReset,
    onSubmit,
    onOpenChange,
    unsavedChangesModalApi,
  };
};
